import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { Receipt, Plus, Trash, PencilSimple, MinusCircle, PlusCircle } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

const emptyDeduction = { name: "", kind: "fixed", value: "", active: true, category: "custom" };
const emptyEarning = { entry_date: format(new Date(), "yyyy-MM-dd"), label: "", amount: "" };

export default function Deductions() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const currency = user?.currency || "CAD";

  const [dedOpen, setDedOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dedForm, setDedForm] = useState(emptyDeduction);
  const [earnOpen, setEarnOpen] = useState(false);
  const [earnForm, setEarnForm] = useState(emptyEarning);

  const { data: deductions = [] } = useQuery({
    queryKey: ["deductions"],
    queryFn: async () => (await api.get("/deductions")).data,
  });
  const { data: earnings = [] } = useQuery({
    queryKey: ["other-earnings"],
    queryFn: async () => (await api.get("/other-earnings")).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["deductions"] });
    qc.invalidateQueries({ queryKey: ["other-earnings"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["projections"] });
  };

  const saveDed = useMutation({
    mutationFn: async (payload) => {
      const body = { ...payload, value: parseFloat(payload.value) || 0 };
      if (editing) return (await api.put(`/deductions/${editing}`, body)).data;
      return (await api.post("/deductions", body)).data;
    },
    onSuccess: () => {
      invalidate();
      toast.success(editing ? "Deduction updated." : "Deduction added.");
      setDedOpen(false); setEditing(null); setDedForm(emptyDeduction);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const toggleDed = useMutation({
    mutationFn: async ({ id, active }) => (await api.put(`/deductions/${id}`, { active })).data,
    onSuccess: invalidate,
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteDed = useMutation({
    mutationFn: async (id) => api.delete(`/deductions/${id}`),
    onSuccess: () => { invalidate(); toast.success("Deduction removed."); },
  });

  const saveEarn = useMutation({
    mutationFn: async (payload) => (await api.post("/other-earnings", { ...payload, amount: parseFloat(payload.amount) || 0 })).data,
    onSuccess: () => { invalidate(); toast.success("Earning added."); setEarnOpen(false); setEarnForm(emptyEarning); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteEarn = useMutation({
    mutationFn: async (id) => api.delete(`/other-earnings/${id}`),
    onSuccess: () => { invalidate(); toast.success("Earning removed."); },
  });

  const openEdit = (d) => {
    setEditing(d.id);
    setDedForm({ name: d.name, kind: d.kind, value: String(d.value), active: d.active, category: d.category || "custom" });
    setDedOpen(true);
  };
  const openNew = () => { setEditing(null); setDedForm(emptyDeduction); setDedOpen(true); };

  const fmtDed = (d) => (d.kind === "percent" ? `${d.value}%` : money(d.value, currency));

  return (
    <div className="animate-fade-up" data-testid="deductions-page">
      <div className="mb-8">
        <p className="overline text-rose-400">Payroll</p>
        <h1 className="text-3xl font-black tracking-tight mt-1">Deductions &amp; Earnings</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Active deductions reduce your estimated net pay. Percentages apply to your gross pay for the period.
        </p>
      </div>

      {/* Deductions */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold flex items-center gap-2">
          <MinusCircle className="h-5 w-5 text-rose-400" weight="bold" /> Deductions
        </h2>
        <Dialog open={dedOpen} onOpenChange={(o) => { setDedOpen(o); if (!o) { setEditing(null); setDedForm(emptyDeduction); } }}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-deduction-button" size="sm" className="rounded-md font-semibold">
              <Plus className="h-4 w-4 mr-1.5" weight="bold" /> Add Deduction
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border" data-testid="deduction-dialog">
            <DialogHeader><DialogTitle>{editing ? "Edit Deduction" : "New Deduction"}</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); saveDed.mutate(dedForm); }} className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input data-testid="deduction-name-input" className="mt-1.5 bg-secondary/50" required
                  value={dedForm.name} onChange={(e) => setDedForm({ ...dedForm, name: e.target.value })} placeholder="e.g. Parking" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={dedForm.kind} onValueChange={(v) => setDedForm({ ...dedForm, kind: v })}>
                    <SelectTrigger className="mt-1.5 bg-secondary/50" data-testid="deduction-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed ($)</SelectItem>
                      <SelectItem value="percent">Percent (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{dedForm.kind === "percent" ? "Percent" : "Amount"}</Label>
                  <Input type="number" step="0.01" min="0" data-testid="deduction-value-input" className="mt-1.5 bg-secondary/50" required
                    value={dedForm.value} onChange={(e) => setDedForm({ ...dedForm, value: e.target.value })}
                    placeholder={dedForm.kind === "percent" ? "15" : "25.00"} />
                </div>
              </div>
              <div className="flex items-center gap-3 bg-secondary/40 rounded-md p-3">
                <Switch checked={dedForm.active} onCheckedChange={(v) => setDedForm({ ...dedForm, active: v })} data-testid="deduction-active-switch" />
                <span className="text-sm">Active (counts toward net pay)</span>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saveDed.isPending} data-testid="deduction-submit" className="rounded-md font-semibold">
                  {saveDed.isPending ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border-border overflow-hidden mb-10">
        {deductions.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="deductions-empty">No deductions yet.</div>
        ) : deductions.map((d) => (
          <div key={d.id} data-testid={`deduction-row-${d.id}`}
            className="flex items-center gap-3 px-5 py-4 border-b border-border last:border-0 hover:bg-secondary/30 transition-colors duration-200">
            <Switch checked={d.active} data-testid={`deduction-toggle-${d.id}`}
              onCheckedChange={(v) => toggleDed.mutate({ id: d.id, active: v })} />
            <div className="min-w-0 flex-1">
              <p className={`font-semibold truncate ${d.active ? "" : "text-muted-foreground"}`}>{d.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{d.category} · {d.kind === "percent" ? "percentage" : "fixed"}</p>
            </div>
            <span className={`font-bold ${d.active ? "text-rose-400" : "text-muted-foreground"}`} data-testid={`deduction-amount-${d.id}`}>
              {fmtDed(d)}
            </span>
            <button onClick={() => openEdit(d)} data-testid={`edit-deduction-${d.id}`}
              className="text-muted-foreground hover:text-primary transition-colors duration-200"><PencilSimple className="h-4 w-4" weight="bold" /></button>
            <button onClick={() => deleteDed.mutate(d.id)} data-testid={`delete-deduction-${d.id}`}
              className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash className="h-4 w-4" weight="bold" /></button>
          </div>
        ))}
      </Card>

      {/* Other Earnings */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold flex items-center gap-2">
          <PlusCircle className="h-5 w-5 text-emerald-400" weight="bold" /> Other Earnings
        </h2>
        <Dialog open={earnOpen} onOpenChange={setEarnOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-earning-button" size="sm" variant="outline" className="rounded-md font-semibold">
              <Plus className="h-4 w-4 mr-1.5" weight="bold" /> Add Earning
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border" data-testid="earning-dialog">
            <DialogHeader><DialogTitle>New Earning</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); saveEarn.mutate(earnForm); }} className="space-y-4">
              <div>
                <Label>Date</Label>
                <div className="mt-1.5"><DatePicker testId="earning-date" value={earnForm.entry_date}
                  onChange={(d) => setEarnForm({ ...earnForm, entry_date: d })} /></div>
              </div>
              <div>
                <Label>Description</Label>
                <Input data-testid="earning-label-input" className="mt-1.5 bg-secondary/50" required
                  value={earnForm.label} onChange={(e) => setEarnForm({ ...earnForm, label: e.target.value })} placeholder="e.g. Retention bonus" />
              </div>
              <div>
                <Label>Amount</Label>
                <Input type="number" step="0.01" min="0" data-testid="earning-amount-input" className="mt-1.5 bg-secondary/50" required
                  value={earnForm.amount} onChange={(e) => setEarnForm({ ...earnForm, amount: e.target.value })} placeholder="100.00" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saveEarn.isPending} data-testid="earning-submit" className="rounded-md font-semibold">
                  {saveEarn.isPending ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border-border overflow-hidden">
        {earnings.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="earnings-empty">No other earnings yet.</div>
        ) : earnings.map((e) => (
          <div key={e.id} data-testid={`earning-row-${e.id}`}
            className="flex items-center gap-3 px-5 py-4 border-b border-border last:border-0 hover:bg-secondary/30 transition-colors duration-200">
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{e.label}</p>
              <p className="text-xs text-muted-foreground">{format(new Date(e.entry_date + "T00:00:00"), "MMM d, yyyy")}</p>
            </div>
            <span className="font-bold text-emerald-400">{money(e.amount, currency)}</span>
            <button onClick={() => deleteEarn.mutate(e.id)} data-testid={`delete-earning-${e.id}`}
              className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash className="h-4 w-4" weight="bold" /></button>
          </div>
        ))}
      </Card>
    </div>
  );
}
